import React from "react";
import RecognitionGuideLayout from "@/components/RecognitionGuideLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Award, Users, TrendingUp, Target, BarChart3, Gift, ArrowRight, Plus, Minus } from "lucide-react";
import recognitionHeroImage from "@/assets/recognition-guide-hero.webp";

export { Page }

const guideSteps = [
  {
    title: "What is Employee Recognition?",
    description: "Understand the fundamentals of workplace recognition and its core principles.",
    icon: Award,
    url: "/employee-recognition-program-guide-assembly/what-is-employee-recognition-assembly"
  },
  {
    title: "Why is Employee Recognition Important?",
    description: "Discover the business impact and benefits of recognition programs.",
    icon: TrendingUp,
    url: "/employee-recognition-program-guide-assembly/why-is-employee-recognition-important-assembly"
  },
  {
    title: "Types of Employee Recognition and Rewards",
    description: "Explore different recognition methods and reward strategies.",
    icon: Gift,
    url: "/employee-recognition-program-guide-assembly/types-of-employee-recognition-and-rewards-assembly"
  },
  {
    title: "Building a Successful Employee Recognition Program",
    description: "Step-by-step guide to creating and implementing your program.",
    icon: Target,
    url: "/employee-recognition-program-guide-assembly/building-a-successful-employee-recognition-program-assembly"
  },
  {
    title: "Employee Recognition Impact",
    description: "Measure and understand the ROI of your recognition efforts.",
    icon: BarChart3,
    url: "/employee-recognition-program-guide-assembly/employee-recognition-impact-assembly"
  },
  {
    title: "Employee Recognition Tools and Resources",
    description: "Essential tools, templates, and resources for success.",
    icon: Users,
    url: "/employee-recognition-program-guide-assembly/employee-recognition-tools-and-resources-assembly"
  }
];

const stats = [
  { value: "31%", label: "Higher Engagement", source: "Gallup", url: "https://www.gallup.com/workplace/236441/employee-recognition-low-cost-high-impact.aspx" },
  { value: "45%", label: "Lower Voluntary Turnover", source: "", url: "" },
  { value: "21%", label: "Increase in Productivity", source: "HBR", url: "https://hbr.org/2011/05/the-power-of-small-wins" }
];

function Page() {
  return (
    <RecognitionGuideLayout
      title="Employee Recognition Program Guide"
      description="Your complete guide to building a recognition culture at work. Learn strategies, best practices, and tools to create effective employee recognition programs that boost engagement and retention."
      currentStep={0}
      showNavigation={false}
    >
      <div className="space-y-12">
        {/* Hero Section */}
        <div className="text-center space-y-8">
          <div className="w-full max-w-4xl mx-auto">
            <img 
              src={recognitionHeroImage} 
              alt="Team celebrating employee recognition with person holding trophy on podium"
              className="w-full h-auto rounded-xl shadow-lg"
            />
          </div>
          
          <div className="space-y-4">
            <p className="text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              Your complete guide to building a recognition culture at work. Learn proven strategies, 
              best practices, and discover the tools you need to create effective employee recognition 
              programs that boost engagement, retention, and performance.
            </p>
          </div>
        </div>

        {/* Introduction Content */}
        <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
          <CardContent className="p-8 space-y-6">
            <p className="text-lg text-foreground leading-relaxed">
              Recognition has always been part of work, but in today's world, it's become much more than a pat on the back. 
              It's a powerful way to{" "}
              <a href="/solutions/recognition" className="text-primary hover:text-primary/80 underline">
                build a recognition culture
              </a>{" "}
              that improves engagement, strengthens retention, and helps people feel genuinely connected to their company's mission.
            </p>
            
            <p className="text-muted-foreground leading-relaxed">
              When employees feel valued, they show up differently. They put in more effort, stay longer, and push harder to deliver great results. 
              Research shows that companies with strong recognition programs see:
            </p>
            
            {/* Stats Grid */}
            <div className="grid md:grid-cols-3 gap-6 my-8">
              {stats.map((stat, index) => (
                <div key={index} className="bg-primary/5 border border-primary/20 p-6 rounded-xl text-center hover:shadow-md transition-all duration-300">
                  <div className="text-3xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-sm font-medium text-foreground mb-1">{stat.label}</div>
                  {stat.source && (
                    <a 
                      href={stat.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:text-primary/80 underline"
                    >
                      ({stat.source})
                    </a>
                  )}
                </div>
              ))}
            </div>
            
            <p className="text-muted-foreground leading-relaxed">
              That's not just a "feel good" benefit-it's a direct impact on the bottom line.
            </p>
            
            <div className="border-l-3 border-primary pl-6 bg-primary/5 py-4 rounded-r-lg">
              <p className="text-foreground leading-relaxed">
                This guide is here to help you understand what effective recognition looks like today. We'll break down the{" "}
                <a href="/employee-recognition/peer-to-peer" className="text-primary hover:text-primary/80 underline">
                  different types of recognition
                </a>, why it matters more than ever, and how you can{" "}
                <a href="/employee-recognition-program-guide-assembly/building-a-successful-employee-recognition-program-assembly" className="text-primary hover:text-primary/80 underline">
                  build a successful recognition program
                </a>{" "}
                that makes appreciation a natural part of everyday work.
              </p>
            </div>
            
            <p className="text-muted-foreground leading-relaxed">
              Along the way, we'll share best practices, strategies from leading organizations, and simple tools to measure recognition impact. Whether you're starting from scratch or looking to strengthen an existing program, this guide will give you a roadmap to create
              recognition that feels authentic, scales with your organization, and delivers real results.
            </p>
          </CardContent>
        </Card>

        {/* Guide Steps Grid */}
        <section>
          <div className="text-center space-y-3 mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Your Complete Recognition Guide
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Follow this step-by-step journey to build a recognition program that drives real results
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guideSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-border/60 bg-card/50 backdrop-blur-sm flex flex-col h-full">
                  <CardHeader className="pb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 border border-primary/20">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg font-semibold leading-tight">
                      {step.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 flex flex-col flex-1">
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-3 flex-1">
                      {step.description}
                    </p>
                    <Button variant="outline" size="sm" asChild className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <a href={step.url} className="flex items-center gap-2">
                        Start Step {index + 1}
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Quick Start Section */}
        <Card className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-primary/20 backdrop-blur-sm">
          <CardContent className="p-8 text-center space-y-6">
            <h2 className="text-2xl font-semibold text-foreground">
              Ready to Get Started?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Jump right into building your recognition program, or start with the fundamentals 
              to ensure you're building on a solid foundation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <a href="/employee-recognition-program-guide-assembly/what-is-employee-recognition-assembly" className="flex items-center gap-2">
                  Start from the Beginning
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="/employee-recognition-program-guide-assembly/building-a-successful-employee-recognition-program-assembly" className="flex items-center gap-2">
                  Jump to Implementation
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Key Benefits Section */}
        <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
          <CardContent className="p-8 space-y-6">
            <h2 className="text-2xl font-bold text-foreground text-center">
              Why This Guide Matters
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">For HR Leaders:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></div>
                    <span className="text-muted-foreground">Build data-driven recognition strategies that improve retention</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></div>
                    <span className="text-muted-foreground">Create scalable programs that work for distributed teams</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></div>
                    <span className="text-muted-foreground">Measure recognition impact with clear ROI metrics</span>
                  </li>
                </ul>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">For Managers:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></div>
                    <span className="text-muted-foreground">Learn when and how to recognize team members effectively</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></div>
                    <span className="text-muted-foreground">Build recognition habits that strengthen team dynamics</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></div>
                    <span className="text-muted-foreground">Use recognition to reinforce company values daily</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Closing Statement */}
        <div className="text-center space-y-4 py-8">
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Ready to transform your workplace culture? This comprehensive guide provides everything you need to build recognition programs that drive engagement, boost retention, and deliver measurable business results.
          </p>
        </div>

        {/* FAQ Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Common questions about employee recognition programs
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {[
              {
                question: "How long does it take to implement an employee recognition program?",
                answer: "With the right platform and clear strategy, basic recognition programs can launch within 2-4 weeks. However, building sustainable recognition habits across your organization typically takes 3-6 months. This guide provides a roadmap to accelerate that timeline."
              },
              {
                question: "What's the typical ROI of employee recognition programs?",
                answer: "Organizations typically see 3-5x ROI from recognition programs through reduced turnover costs, increased productivity, and improved engagement scores. The exact ROI varies by industry and implementation quality, but most companies break even within 6-12 months."
              },
              {
                question: "Do recognition programs work for remote and hybrid teams?",
                answer: "Yes, recognition is especially crucial for distributed teams where casual appreciation moments are less frequent. Digital recognition platforms excel at creating visibility and connection across locations, time zones, and work arrangements."
              },
              {
                question: "How much should companies budget for employee recognition?",
                answer: "Most organizations budget 1-2% of payroll for recognition programs, including platform costs and rewards. However, the most impactful recognition-peer-to-peer appreciation and values-based recognition-requires minimal budget. Our guide covers budget planning strategies for any size organization."
              }
            ].map((faq, index) => (
              <Collapsible key={index}>
                <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
                  <CollapsibleTrigger className="w-full group">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-left flex items-center justify-between">
                        <span className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {faq.question}
                        </span>
                        <div className="flex items-center">
                          <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=open]:hidden" />
                          <Minus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=closed]:hidden" />
                        </div>
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        </section>
      </div>
    </RecognitionGuideLayout>
  );
}